/**
 * CallbackSlotPicker — "Find a time": a week of one recruiter's calendar with
 * their other appointments on it, so a free spot can be found by eye and the
 * callback placed with a click (owner ask 2).
 *
 * Opened from the callback card's book and edit forms (and from the overlap
 * alert's "Find another time" link). Columns are the tenant's booking days
 * (Mon–Fri by default), rows are quarter hours from an hour before the business
 * day to an hour after (clamped to 6 AM – 9 PM); below `sm` it shows one day at
 * a time. What it draws, from GET /api/crm/callbacks/availability:
 *   - the business-hours band (white) vs outside hours (shaded; booking there
 *     still works, the form's outside-hours confirm applies);
 *   - booked callbacks as cyan blocks with the lead's name (the calendar's
 *     callback hue), calendar blocks as slate (your own, titled) or hatched
 *     grey "Busy" (a teammate's private block);
 *   - out-of-office days and closed dates greyed with a label; past times
 *     disabled; in edit mode the call as booked now in a dashed outline.
 * Hovering a free cell shows a ghost of the call's length; clicking selects it,
 * and the footer reads "Tue, Oct 6 · 10:30–11:00 AM with Maria" with Use this
 * time. An occupied cell can be chosen too, behind the same one-press Override
 * as the form (no note). Picks snap to 15 minutes, so every time fits the
 * form's :00/:15/:30/:45 minute dropdown. Times are the browser's local time.
 *
 * TEAM MODE (owner ask 2026-10-16, the recruiters' "for scheduling" half of My
 * Calendar's Team day): the Week | Team switch shows ONE booking day with a
 * column per recruiter on the lead's study roster ("Study recruiters"), or the
 * whole team ("Everyone"), side by side (TeamSlotGrid.tsx). Clicking a cell in
 * someone's column picks that recruiter AND that time; the footer, Override and
 * Use this time are the Week view's. One availability read per shown recruiter
 * for that day, kept per (recruiter, day) while the picker is open. The mode is
 * remembered per browser (`crm.callbackSlots.mode`).
 *
 * Plain grid + absolute positioning; no calendar dependency. The dialog
 * follows the CRM modal recipe (aria-modal, Escape and overlay close, focus
 * moved in and restored, body scroll lock, dark mode).
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight, Columns, Loader2, UserMinus, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import SegmentedControl from '../../../ui/SegmentedControl';
import { getPref, setPref, useIsPhone, useTeam } from '../../lib';
import { outTodaySuffix } from '../OwnerSelect';
import { Z, btnGhost, btnPrimary, fieldCls, iconBtn } from '../ui';
import { getCallbackAvailability, getStudyRecruiters } from './api';
import SlotConflictAlert from './SlotConflictAlert';
import { DayColumn, HATCH, Legend, ROW_PX, px } from './SlotColumn';
import TeamSlotGrid, { TeamColumnState } from './TeamSlotGrid';
import {
  AvailabilityResponse,
  BookingWindow,
  Busy,
  Conflict,
  FALLBACK_WINDOW,
  GridCell,
  addDays,
  dayCells,
  dayShortLabel,
  gridBounds,
  hourLabel,
  inBusinessHours,
  isAbsentDay,
  isClosedDate,
  minuteInDay,
  minutesOf,
  overlapsFor,
  parseLocalInput,
  rangeLabel,
  snapToBookingDay,
  startOfDay,
  startOfWeek,
  stepBookingDay,
  tenantDayKeyOf,
  toLocalInput,
  weekDays,
  weekRange,
  weekdayShort,
  selectionLabel,
} from './slotLogic';
import { TeamSource, dayRange, teamGridBounds, teamPick, teamPickerColumns } from './teamSlotLogic';

export interface SlotPick {
  /** "YYYY-MM-DDTHH:MM" (local), ready for the form's DateTimeFields. */
  value: string;
  assignee: string;
  /** The pick overlaps something and the recruiter pressed Override. */
  overridden: boolean;
}

export interface CallbackSlotPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** The form's current value ('' = none yet): the week opens on it and it shows selected. */
  value: string;
  /** The form's recruiter ('' = the viewer). */
  assignee: string;
  /** The call's length (edit mode); absent = the tenant's slot length. */
  durationMin?: number;
  /** The callback being edited, left out of the busy list. */
  excludeCallbackId?: string | null;
  /** Edit mode: the call as booked now, drawn in a dashed outline on its recruiter's grid. */
  current?: { start: string; durationMin: number; assignee: string | null } | null;
  /** The lead's study (its focus): Team mode offers that study's recruiter roster. Absent = everyone. */
  studyId?: string | null;
  onPick: (pick: SlotPick) => void;
}

export default function CallbackSlotPicker(props: CallbackSlotPickerProps) {
  if (!props.isOpen) return null;
  // Mounted per open, so every open starts from the form's current value.
  return <PickerDialog {...props} />;
}

type Loaded = { key: string; data: AvailabilityResponse | null; error: boolean };
type Mode = 'week' | 'team';
type Roster = { ok: true; ids: string[]; names: Map<string, string> } | { ok: false };

const MODE_PREF = 'callbackSlots.mode';

function PickerDialog({ onClose, value, assignee, durationMin, excludeCallbackId, current, studyId, onPick }: CallbackSlotPickerProps) {
  const { user } = useAuth();
  const { team, byId } = useTeam();
  const isPhone = useIsPhone();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const initial = parseLocalInput(value);
  const [recruiter, setRecruiter] = useState<string>(assignee || user?.id || '');
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(initial || new Date()));
  const [sel, setSel] = useState<{ ms: number; conflicts: Conflict[] } | null>(() =>
    initial && initial.getTime() >= Date.now() ? { ms: initial.getTime(), conflicts: [] } : null,
  );
  const [hover, setHover] = useState<number | null>(null);
  const [overridden, setOverridden] = useState(false);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  // The booking window outlives any one read, so the grid keeps its shape (and
  // the phone's day never oscillates) while the next week or recruiter loads.
  const [win, setWin] = useState<BookingWindow>(FALLBACK_WINDOW);

  // Team mode: whose columns, the study roster (read once per open), the
  // per-(recruiter, day) reads, and the hovered cell's column.
  const [mode, setModeState] = useState<Mode>(() => (getPref(MODE_PREF, 'week') === 'team' ? 'team' : 'week'));
  const [source, setSource] = useState<TeamSource>('roster');
  const [roster, setRoster] = useState<Roster | null>(null);
  const [teamLoaded, setTeamLoaded] = useState<Record<string, Loaded>>({});
  const [teamHover, setTeamHover] = useState<{ col: string; ms: number } | null>(null);
  const teamMode = mode === 'team';
  const oneDay = isPhone || teamMode;

  const shownDay = snapToBookingDay(anchor, win);
  const range = weekRange(isPhone ? shownDay : anchor);
  const fetchKey = !teamMode && recruiter ? `${recruiter}|${range.from}|${reloadTick}` : null;

  useEffect(() => {
    if (!fetchKey) return undefined;
    const ctrl = new AbortController();
    let alive = true;
    getCallbackAvailability({ assignee: recruiter, from: range.from, to: range.to, exclude: excludeCallbackId || null }, ctrl.signal)
      .then((data) => {
        if (!alive) return;
        setLoaded({ key: fetchKey, data, error: false });
        setWin(data.booking);
      })
      .catch(() => {
        if (alive) setLoaded({ key: fetchKey, data: null, error: true });
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
    // fetchKey folds in the recruiter, the week and the retry tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  // The study roster, the first time Team mode needs it. A failed read falls
  // back to everyone (no retry: the team is a fine answer).
  const needRoster = teamMode && !!studyId && roster === null;
  useEffect(() => {
    if (!needRoster || !studyId) return undefined;
    const ctrl = new AbortController();
    let alive = true;
    getStudyRecruiters(studyId, ctrl.signal)
      .then((rows) => {
        if (!alive) return;
        const names = new Map<string, string>();
        rows.forEach((r) => { if (r.name) names.set(r.id, r.name); });
        setRoster({ ok: true, ids: rows.map((r) => r.id), names });
      })
      .catch(() => {
        if (alive) setRoster({ ok: false });
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [needRoster, studyId]);

  const rosterReady = roster && roster.ok && roster.ids.length > 0 ? roster : null;
  const rosterNames = rosterReady?.names;
  // The form's recruiter keeps a column whoever else is shown (it never moves with a click).
  const formRecruiter = assignee || user?.id || null;
  const columns = useMemo(
    () => (teamMode && !needRoster
      ? teamPickerColumns(team, {
        rosterIds: source === 'roster' && rosterReady ? rosterReady.ids : null,
        current: formRecruiter,
        meId: user?.id,
        names: rosterNames,
      })
      : []),
    [teamMode, needRoster, team, source, rosterReady, rosterNames, formRecruiter, user?.id],
  );
  const colIds = columns.map((c) => c.id).filter((id): id is string => !!id);
  const day = dayRange(shownDay);
  const teamKeyOf = (id: string) => `${id}|${day.from}|${reloadTick}`;
  const teamFetchKey = teamMode && colIds.length ? `${[...colIds].sort().join(',')}|${day.from}|${reloadTick}` : null;

  // One availability read per shown recruiter for the day. Each (recruiter,
  // day) is read once while the picker is open: a read in flight is not
  // restarted when the columns change (Everyone adds some), only abandoned
  // when the picker closes.
  const inFlight = useRef(new Map<string, AbortController>());
  useEffect(() => {
    const flights = inFlight.current;
    return () => {
      flights.forEach((c) => c.abort());
      // Cleared too, so StrictMode's dev remount reads again instead of waiting on aborted reads.
      flights.clear();
    };
  }, []);
  useEffect(() => {
    if (!teamFetchKey) return;
    const flights = inFlight.current;
    for (const id of colIds) {
      const key = teamKeyOf(id);
      if (teamLoaded[key] || flights.has(key)) continue;
      const ctrl = new AbortController();
      flights.set(key, ctrl);
      getCallbackAvailability({ assignee: id, from: day.from, to: day.to, exclude: excludeCallbackId || null }, ctrl.signal)
        .then((data) => {
          if (ctrl.signal.aborted) return;
          setTeamLoaded((prev) => ({ ...prev, [key]: { key, data, error: false } }));
          setWin(data.booking);
        })
        .catch(() => {
          if (!ctrl.signal.aborted) setTeamLoaded((prev) => ({ ...prev, [key]: { key, data: null, error: true } }));
        })
        .finally(() => {
          if (flights.get(key) === ctrl) flights.delete(key);
        });
    }
    // teamFetchKey folds in the columns, the day and the retry tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFetchKey]);

  const teamStateOf = (id: string): TeamColumnState => {
    const l = teamLoaded[teamKeyOf(id)];
    return { data: l?.data || null, loading: !l, error: !!l?.error };
  };
  const teamFirst = colIds.map((id) => teamLoaded[teamKeyOf(id)]?.data).find((d): d is AvailabilityResponse => !!d) || null;
  const teamErrors = colIds.filter((id) => teamLoaded[teamKeyOf(id)]?.error);

  const data = loaded?.key === fetchKey ? loaded.data : null;
  const loading = teamMode
    ? needRoster || colIds.some((id) => !teamLoaded[teamKeyOf(id)])
    : !!fetchKey && loaded?.key !== fetchKey;
  const loadError = loaded?.key === fetchKey && loaded.error;
  // The read that speaks for the selection: the week's, or the chosen recruiter's day.
  const activeData = teamMode ? teamLoaded[teamKeyOf(recruiter)]?.data || null : data;
  const w = (teamMode ? teamFirst?.booking : data?.booking) || win;
  const dur = durationMin ?? w.slot_minutes;
  const timezone = teamMode ? teamFirst?.timezone : data?.timezone;
  const dayKeyOf = useMemo(() => tenantDayKeyOf(timezone), [timezone]);
  const teamBusy: Busy[] = teamMode ? colIds.flatMap((id) => teamLoaded[teamKeyOf(id)]?.data?.busy || []) : [];
  const bounds = teamMode ? teamGridBounds(w, teamBusy, shownDay) : gridBounds(w);
  const days = isPhone ? [shownDay] : weekDays(anchor, w);
  const busy: Busy[] = data?.busy || [];
  const absentDays = data?.absent_days || [];
  const now = Date.now();
  const fromMs = Date.parse(teamMode ? day.from : range.from);
  const toMs = Date.parse(teamMode ? day.to : range.to);
  const inView = (ms: number) => ms >= fromMs && ms < toMs;

  const isMe = !!user?.id && recruiter === user.id;
  const name = isMe ? 'you' : byId.get(recruiter)?.name || rosterNames?.get(recruiter) || null;
  const possessive = isMe ? 'your' : name ? `${name}'s` : "the recruiter's";

  // The selection's conflicts come from the loaded read when it is in view,
  // else from what the cell said when it was clicked.
  const selConflicts: Conflict[] = !sel
    ? []
    : activeData && inView(sel.ms)
      ? overlapsFor(activeData.busy, activeData.absent_days, sel.ms, dur, dayKeyOf)
      : sel.conflicts;
  const selDay = sel ? startOfDay(new Date(sel.ms)) : null;
  const selOutside = sel && selDay ? !inBusinessHours(selDay, minuteInDay(sel.ms, selDay), dur, w, dayKeyOf) : false;
  const canUse = !!sel && sel.ms >= now && (selConflicts.length === 0 || overridden);

  // Escape closes; the body stops scrolling behind the dialog; focus moves in
  // and goes back to whatever opened the picker.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [onClose]);

  // Once per loaded week (or Team day), scroll the grid to the selection (when
  // it is in view) or the start of the business day.
  const scrolledFor = useRef<string | null>(null);
  const scrollKey = teamMode ? (teamFirst ? `team|${day.from}` : null) : data ? fetchKey : null;
  useEffect(() => {
    if (!scrollKey || scrolledFor.current === scrollKey || !bodyRef.current) return;
    scrolledFor.current = scrollKey;
    const target = sel && inView(sel.ms) ? minuteInDay(sel.ms, startOfDay(new Date(sel.ms))) : minutesOf(w.day_start);
    bodyRef.current.scrollTop = Math.max(0, px(target - bounds.startMin) - ROW_PX * 4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollKey]);

  const choose = (cell: GridCell) => {
    if (cell.state === 'past') return;
    setSel({ ms: cell.startMs, conflicts: cell.conflicts });
    setOverridden(false);
  };

  // Team mode: a cell in someone's column picks them and the time.
  const chooseInColumn = (columnId: string, cell: GridCell) => {
    const pick = teamPick(cell, columnId);
    if (!pick) return;
    setRecruiter(pick.assignee);
    setSel({ ms: pick.ms, conflicts: pick.conflicts });
    setOverridden(false);
  };

  const changeRecruiter = (id: string) => {
    setRecruiter(id);
    setOverridden(false);
    // Bring the selection back into view so its conflicts are re-read for them.
    if (sel) setAnchor(startOfDay(new Date(sel.ms)));
  };

  const changeMode = (v: string) => {
    const next: Mode = v === 'team' ? 'team' : 'week';
    setModeState(next);
    setPref(MODE_PREF, next);
    setHover(null);
    setTeamHover(null);
    // Open the other view on the selection's day, so it stays in view.
    if (sel) setAnchor(startOfDay(new Date(sel.ms)));
  };

  const use = () => {
    if (!sel || !canUse) return;
    onPick({ value: toLocalInput(new Date(sel.ms)), assignee: recruiter, overridden: selConflicts.length > 0 && overridden });
    onClose();
  };

  // Navigation: a week at a time from `sm` up, a booking day at a time below
  // (and always in Team mode).
  const today = startOfDay(new Date());
  const atStart = oneDay ? shownDay.getTime() <= snapToBookingDay(today, w).getTime() : startOfWeek(anchor).getTime() <= startOfWeek(today).getTime();
  const goPrev = () => setAnchor(oneDay ? stepBookingDay(shownDay, -1, w) : addDays(startOfWeek(anchor), -7));
  const goNext = () => setAnchor(oneDay ? stepBookingDay(shownDay, 1, w) : addDays(startOfWeek(anchor), 7));
  const goToday = () => setAnchor(today);
  const dayClosed = teamMode && isClosedDate(shownDay, w, dayKeyOf);
  const periodLabel = oneDay
    ? `${dayShortLabel(shownDay)}${dayClosed ? ' · Office closed' : ''}`
    : rangeLabel(days[0], days[days.length - 1]);

  const gridCols = { gridTemplateColumns: `3.25rem repeat(${days.length}, minmax(0, 1fr))` };
  const gridHeight = bounds.rows * ROW_PX;
  const hours: number[] = [];
  for (let m = Math.ceil(bounds.startMin / 60) * 60; m < bounds.endMin; m += 60) hours.push(m);

  const recruiterInTeam = team.some((m) => m.id === recruiter);
  const errorWho = teamErrors.length === 1
    ? `${columns.find((c) => c.id === teamErrors[0])?.name || 'one recruiter'}'s calendar`
    : `${teamErrors.length} recruiters' calendars`;

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm sm:items-center`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="my-8 flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white font-inter shadow-xl focus:outline-none dark:bg-gray-800"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100">Find a time</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {teamMode
                ? `Pick a free spot in anyone's column: that sets the recruiter and the time. A ${dur}-minute call; times are your local time.`
                : `Pick a free spot on ${possessive} calendar. A ${dur}-minute call; times are your local time.`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar: period navigation, view, recruiter (Week) or whose columns (Team), legend */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={goPrev} disabled={atStart} aria-label={oneDay ? 'Previous day' : 'Previous week'} className={iconBtn}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button type="button" onClick={goToday} className={btnGhost}>
              {oneDay ? 'Today' : 'This week'}
            </button>
            <button type="button" onClick={goNext} aria-label={oneDay ? 'Next day' : 'Next week'} className={iconBtn}>
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="ml-1 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100" aria-live="polite">
              {periodLabel}
            </span>
            {loading && <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading calendar" />}
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
            <SegmentedControl
              label="Calendar view"
              value={mode}
              onChange={changeMode}
              options={[
                { value: 'week', label: 'Week', icon: <CalendarRange className="h-4 w-4" />, title: "One recruiter's week" },
                { value: 'team', label: 'Team', icon: <Columns className="h-4 w-4" />, title: 'Everyone side by side, one day' },
              ]}
            />
            {!teamMode ? (
              <select
                value={recruiter}
                onChange={(e) => changeRecruiter(e.target.value)}
                aria-label="Recruiter"
                className={`${fieldCls} min-h-[44px] min-w-0 flex-1 cursor-pointer sm:min-h-0 sm:w-60 sm:flex-none`}
              >
                {!recruiter && <option value="" disabled>Choose a recruiter</option>}
                {recruiter && !recruiterInTeam && <option value={recruiter}>{isMe ? 'Me' : 'Current recruiter'}</option>}
                {team.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.id === user?.id ? `${m.name} (me)` : m.name}{outTodaySuffix(m)}
                  </option>
                ))}
              </select>
            ) : rosterReady ? (
              <SegmentedControl
                label="Whose columns"
                value={source}
                onChange={(v) => { setSource(v === 'team' ? 'team' : 'roster'); setTeamHover(null); }}
                options={[
                  { value: 'roster', label: 'Study recruiters', title: "The recruiters on this lead's study" },
                  { value: 'team', label: 'Everyone', title: 'Everyone in the CRM' },
                ]}
              />
            ) : null}
          </div>
          <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
            <Legend swatch="border-l-2 border-cyan-500 bg-cyan-100 dark:bg-cyan-900/50" label="Callback" />
            <Legend swatch={`border border-gray-300 dark:border-gray-600 ${HATCH}`} label="Busy" />
            <Legend swatch="bg-gray-100 dark:bg-gray-900/60" label="Outside business hours" />
            <Legend swatch="border border-dashed border-gray-400 bg-gray-200/60 dark:border-gray-500 dark:bg-gray-700/40" label="Out of office / closed" />
            {current && <Legend swatch="border-2 border-dashed border-primary-500" label="This call now" />}
          </div>
        </div>

        {(teamMode ? teamErrors.length > 0 : loadError) && (
          <div className="flex flex-wrap items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            Couldn&apos;t load {teamMode ? errorWho : `${possessive} calendar`}. You can still pick a time; it is checked when you save.
            <button
              type="button"
              onClick={() => setReloadTick((n) => n + 1)}
              className="font-medium underline underline-offset-2 hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Retry
            </button>
          </div>
        )}

        {/* The grid */}
        <div ref={bodyRef} className={`relative min-h-0 flex-1 ${teamMode ? 'overflow-auto' : 'overflow-y-auto'}`}>
          {teamMode ? (
            needRoster ? (
              <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">Loading the study&apos;s recruiters…</p>
            ) : columns.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No recruiters to show.</p>
            ) : (
              <TeamSlotGrid
                day={shownDay}
                columns={columns}
                stateOf={teamStateOf}
                bounds={bounds}
                w={w}
                dur={dur}
                dayKeyOf={dayKeyOf}
                closed={dayClosed}
                now={now}
                isPhone={isPhone}
                hover={teamHover}
                setHover={setTeamHover}
                sel={sel ? { assignee: recruiter, ms: sel.ms, conflicted: selConflicts.length > 0 } : null}
                current={current || null}
                onChoose={chooseInColumn}
              />
            )
          ) : !recruiter ? (
            <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">Choose a recruiter to see their calendar.</p>
          ) : (
            <>
              <div
                className="sticky top-0 z-10 grid border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                style={gridCols}
              >
                <div />
                {days.map((d) => {
                  const isToday = d.getTime() === today.getTime();
                  const absent = isAbsentDay(d, absentDays, dayKeyOf);
                  const closed = isClosedDate(d, w, dayKeyOf);
                  return (
                    <div key={d.getTime()} className="border-l border-gray-200 px-1 py-1.5 text-center dark:border-gray-700">
                      <div className={`text-xs ${isToday ? 'font-semibold text-primary-700 dark:text-primary-400' : 'font-medium text-gray-700 dark:text-gray-300'}`}>
                        {weekdayShort(d)} <span className="tabular-nums">{d.getDate()}</span>
                      </div>
                      {(absent || closed) && (
                        <div className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-medium text-gray-500 dark:text-gray-400">
                          {absent && <UserMinus className="h-3 w-3" aria-hidden="true" />}
                          {absent ? 'Out of office' : 'Closed'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="grid" style={gridCols} data-slot-grid>
                {/* Time column */}
                <div className="relative" style={{ height: gridHeight }} aria-hidden="true">
                  {hours.map((m) => (
                    <span
                      key={m}
                      className="absolute right-1.5 -translate-y-1/2 whitespace-nowrap text-[10px] tabular-nums text-gray-400 dark:text-gray-500"
                      style={{ top: px(m - bounds.startMin) }}
                    >
                      {m === bounds.startMin ? '' : hourLabel(m)}
                    </span>
                  ))}
                </div>
                {days.map((d) => (
                  <DayColumn
                    key={d.getTime()}
                    day={d}
                    cells={dayCells(d, bounds, w, dur, busy, absentDays, dayKeyOf, now)}
                    height={gridHeight}
                    bounds={bounds}
                    busy={busy}
                    dur={dur}
                    greyed={isAbsentDay(d, absentDays, dayKeyOf) || isClosedDate(d, w, dayKeyOf)}
                    loading={loading}
                    now={now}
                    hover={hover}
                    setHover={setHover}
                    sel={sel ? { ms: sel.ms, conflicted: selConflicts.length > 0 } : null}
                    current={current && current.assignee === recruiter ? current : null}
                    onChoose={choose}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer: the selection, its overlap alert, and the two buttons */}
        <div className="flex flex-col gap-3 border-t border-gray-100 p-4 dark:border-gray-700 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1 text-sm" aria-live="polite">
            {sel ? (
              <>
                <p className="font-medium text-gray-900 dark:text-gray-100" data-testid="slot-picker-selection">
                  {selectionLabel(sel.ms, dur, name)}
                </p>
                {selOutside && (
                  <p className="mt-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                    Outside business hours: booking it asks you to confirm.
                  </p>
                )}
                <SlotConflictAlert
                  conflicts={selConflicts}
                  overridden={overridden && selConflicts.length > 0}
                  name={name}
                  isMe={isMe}
                  onOverride={() => setOverridden(true)}
                />
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">
                {teamMode
                  ? `Click a free spot in anyone's column to choose them and the time. Hover to preview a ${dur}-minute call.`
                  : `Click a free spot to choose it. Hover to preview a ${dur}-minute call.`}
              </p>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center justify-end gap-2">
            <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
            <button type="button" onClick={use} disabled={!canUse} className={`${btnPrimary} disabled:cursor-not-allowed`}>
              Use this time
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
